import React from 'react';
import { Users } from 'lucide-react';
import FormDrawer from '../FormDrawer';
import Avatar from '../Avatar';
import EmptyState from '../ui/EmptyState';
import {
  displayName,
  utilizationStatusTextClass,
  formatDateShort,
} from './utilizationHelpers';

/**
 * Resolve the value shown on the right-hand side of a user row. Centralised
 * so each caller only has to pass a `metric` string (or a custom
 * `getRowValue`) rather than re-implementing the formatting dance.
 */
const resolveRowValue = (user, metric, getRowValue) => {
  if (typeof getRowValue === 'function') {
    const custom = getRowValue(user);
    if (custom) return custom;
  }
  const util = user.utilization || {};
  const pct = Number(util.percent || 0);
  // "week" metrics are the range-aware figures (backend scopes them to the
  // active filter); "tracked_hours" / "billable_hours" are kept as all-time
  // escape hatches for callers that explicitly want lifetime numbers.
  switch (metric) {
    case 'week_hours':
      return {
        value: `${Number(util.week_hours || 0).toFixed(1)}h`,
        label: 'tracked',
      };
    case 'billable_week_hours':
      return {
        value: `${Number(user.billable_week_hours || 0).toFixed(1)}h`,
        label: 'billable',
      };
    case 'non_billable_week_hours':
      return {
        value: `${Number(user.non_billable_week_hours || 0).toFixed(1)}h`,
        label: 'non-billable',
      };
    case 'tracked_hours':
      return {
        value: `${Number(user.tracked_hours || 0).toFixed(1)}h`,
        label: 'tracked (lifetime)',
      };
    case 'billable_hours':
      return {
        value: `${Number(user.billable_hours || 0).toFixed(1)}h`,
        label: 'billable (lifetime)',
      };
    case 'non_billable_hours':
      return {
        value: `${Number(user.non_billable_hours || 0).toFixed(1)}h`,
        label: 'non-billable (lifetime)',
      };
    case 'free_date':
      return {
        value: user.free_date ? formatDateShort(user.free_date) : '—',
        label: 'free from',
      };
    case 'remaining': {
      const target = Number(
        util.target_hours != null
          ? util.target_hours
          : util.weekly_target_hours != null
            ? util.weekly_target_hours
            : 40,
      );
      // Range-aware remaining = range target minus range tracked.
      const remaining = target - Number(util.week_hours || 0);
      return {
        value: `${Math.abs(remaining).toFixed(1)}h`,
        label: remaining >= 0 ? 'remaining' : 'over',
        isNegative: remaining < 0,
      };
    }
    case 'utilization':
    default:
      return {
        value: `${pct}%`,
        label: 'utilization',
        toneClass: utilizationStatusTextClass(util.status),
      };
  }
};

/** Compute the sort key for the drawer's list (DESC). */
const scoreUser = (user, metric, getSortScore) => {
  if (typeof getSortScore === 'function') {
    const custom = getSortScore(user);
    if (typeof custom === 'number') return custom;
  }
  const util = user.utilization || {};
  switch (metric) {
    case 'week_hours':
      return Number(util.week_hours || 0);
    case 'billable_week_hours':
      return Number(user.billable_week_hours || 0);
    case 'non_billable_week_hours':
      return Number(user.non_billable_week_hours || 0);
    case 'tracked_hours':
      return Number(user.tracked_hours || 0);
    case 'billable_hours':
      return Number(user.billable_hours || 0);
    case 'non_billable_hours':
      return Number(user.non_billable_hours || 0);
    case 'remaining': {
      const target = Number(
        util.target_hours != null
          ? util.target_hours
          : util.weekly_target_hours != null
            ? util.weekly_target_hours
            : 40,
      );
      return target - Number(util.week_hours || 0);
    }
    case 'free_date':
      // Nearest free date first (smallest timestamp = soonest).
      return user.free_date ? -new Date(user.free_date).getTime() : -Infinity;
    case 'utilization':
    default:
      return Number(util.percent || 0);
  }
};

const UserRow = ({ user, metric, getRowValue }) => {
  const sub = [user.department, user.cohort, user.team_lead]
    .filter((v) => v && String(v).trim() !== '')
    .join(' · ');
  const { value, label, toneClass, isNegative } = resolveRowValue(
    user,
    metric,
    getRowValue,
  );
  return (
    <li
      data-testid={`insights-drawer-row-${user.id}`}
      className="py-2.5 px-3 flex items-center gap-3"
    >
      <Avatar
        blobName={user.avatar}
        alt={displayName(user)}
        size="sm"
        fallbackText={displayName(user)}
      />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm text-secondary-900 dark:text-white truncate">
          {displayName(user)}
        </div>
        <div
          className="text-[11px] text-secondary-500 dark:text-secondary-400 truncate"
          title={sub}
        >
          {sub || 'No department'}
        </div>
      </div>
      <div className="text-right ml-2 shrink-0">
        <div
          className={`text-sm font-semibold tabular-nums ${
            toneClass ||
            (isNegative
              ? 'text-red-600 dark:text-red-400'
              : 'text-secondary-900 dark:text-white')
          }`}
        >
          {value}
        </div>
        <div className="text-[10px] uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
          {label}
        </div>
      </div>
    </li>
  );
};

/**
 * Right-side slide-in drawer used by the Insights view to show the people
 * (or projects) that make up any clicked tile. Takes advantage of the
 * existing FormDrawer chrome (portal + Esc + backdrop + scroll lock) so the
 * drill-in interaction feels native to the rest of the app.
 */
const InsightsDetailDrawer = ({
  open,
  onClose,
  title = '',
  subtitle,
  users = [],
  metric = 'utilization',
  icon,
  emptyMessage = 'No employees match this breakdown.',
  getRowValue,
  getSortScore,
}) => {
  const sorted = React.useMemo(() => {
    if (!open) return [];
    const copy = [...users];
    return copy.sort(
      (a, b) =>
        scoreUser(b, metric, getSortScore) - scoreUser(a, metric, getSortScore),
    );
  }, [open, users, metric, getSortScore]);

  const headerSubtitle =
    subtitle ||
    `${users.length} ${users.length === 1 ? 'employee' : 'employees'}`;

  return (
    <FormDrawer
      open={!!open}
      onClose={onClose}
      title={title}
      subtitle={headerSubtitle}
      icon={icon || <Users className="h-5 w-5" />}
      iconColor="primary"
      widthClass="sm:max-w-md lg:max-w-lg"
    >
      <div data-testid="insights-detail-drawer-body">
        {sorted.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nothing to show"
            description={emptyMessage}
          />
        ) : (
          <ul className="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 divide-y divide-secondary-100 dark:divide-secondary-800">
            {sorted.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                metric={metric}
                getRowValue={getRowValue}
              />
            ))}
          </ul>
        )}
      </div>
    </FormDrawer>
  );
};

export default InsightsDetailDrawer;
